import { useEffect, useState } from "react"
import { ModalPopUp } from "../../common/element/ModalPopUp"
import { MenuClient } from "../MenuClient"
import { Header } from "../../common/header/Header"
import { IMG } from "../../common/CONST"
import { IModalMessage } from "../../../types/error/IModalMessage"
import { useNavigate } from "react-router-dom"
import { IReplenishmentListProjection } from "../../../types/replenishment/IReplenishmentListProjection"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { getMime, getMskTime, round2Digits, spacedNum } from "../../common/CommonHelper"
import { ReplenishmentStatus } from "../../../types/replenishment/ReplenishmentStatus"
import { IReplenishmentStatus } from "../../../types/replenishment/IReplenishmentStatus"
import { IPageResponse } from "../../../types/IPageResponse"
import { ICheckConversationResponse } from "../../../types/messenger/ICheckConversationResponse"
import { Messenger } from "../../common/messenger/Messenger"
import { CONFIG } from "../../../CONFIG"
import Button from "@/ui/components/Button"
import Skeleton from "@/ui/components/Skeleton"
import EmptyRow from "@/ui/components/EmptyRow"
import { getReplenishmentViewRoute, REPLENISHMENT_NEW_ROUTE } from "@/routes"
import Input from "@/ui/components/Input"
import AXIOS from "@/requests/_axios"

const getRowClazz = (status: string) => {
  switch (status) {
    case "NEW":
    case "DOC_ACC":
    case "CR_ACC":
    case "CR_AWT":
      return "global-item-active"
    case "DONE":
      return "global-item-done"
    default:
      return "global-item"
  }
}

const getStatusClazz = (status: string) => {
  switch (status) {
    case "DOC_NAC":
    case "CR_NAC":
      return "status-no"
    case "NEW":
    case "DOC_ACC":
    case "BILL":
    case "CR_AWT":
    case "CR_ACC":
      return "status-ok"
    default:
      return "status"
  }
}

export const ClientReplenishmentList = () => {
  const navigate = useNavigate()

  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [replenishments, setReplenishments] = useState<IReplenishmentListProjection[]>([])
  const [search, setSearch] = useState("")
  const [filteredReplenishments, setFilteredReplenishments] = useState<IReplenishmentListProjection[]>([])
  const [status, setStatus] = useState("")
  const [isLoading, setLoading] = useState(true)
  const [isRefetchLoading, setRefetchLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  /* MESSENGER FCN */
  const [instanceId, setInstanceId] = useState<string>()
  const [conversationUserId, setConversationUserId] = useState<number>()
  const CHAT_INSTANCE = "REPLENISHMENT"
  const [unreadList, setUnreadList] = useState<IPageResponse<ICheckConversationResponse>>()

  const fetchUnread = () => {
    TrinityAPI.getUnreadConversations(CHAT_INSTANCE, response => {
      if (response.status === 200) {
        setUnreadList(response.data)
      }
    })
  }

  const closeMessenger = () => {
    setInstanceId(undefined)
    setConversationUserId(undefined)
  }

  const showMessenger = (instanceId: string | undefined, userId: number | undefined) => {
    setInstanceId(instanceId)
    setConversationUserId(userId)
  }

  const getUnreadCounter = (instanceId: string | undefined): number => {
    let counter = 0
    if (!instanceId) return counter
    let unreadItem = unreadList?.items.filter(item => item.instanceId === instanceId)[0]
    counter = unreadItem ? unreadItem.num : 0
    return counter
  }

  /* EOF MESSENGER FCN */

  const fetchReplenishments = () => {
    setSearch("")

    TrinityAPI.getReplenishmentList(
      status,
      response => {
        if (response.status === 200) {
          setReplenishments(response.data)
          setFilteredReplenishments(response.data)
        } else {
          setModalMessage({
            title: "Ошибка!",
            text: `Ошибка получения пополнений: ${response.data.message}`
          })
        }
      },
      () => {
        setLoading(false)
        setRefetchLoading(false)
      }
    )
  }

  const refetchData = () => {
    setRefetchLoading(true)
    fetchReplenishments()
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const response = await AXIOS.get("/trinity/api/v1/unload/xls/replenishments")

      if (response.status === 200) {
        const { data } = response

        const byteCharacters = atob(data.fileBase64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)

        const blob = new Blob([byteArray], { type: getMime(data.extension) })
        const blobUrl = URL.createObjectURL(blob)

        const a = document.createElement("a")
        a.href = blobUrl
        a.download = `${data.fileName}`
        a.click()

        URL.revokeObjectURL(blobUrl)
      }
    } catch (err) {
      setIsDownloading(false)
    }
    setIsDownloading(false)
  }

  const navigateToReplenishment = (id: number) => {
    window.open(getReplenishmentViewRoute(id), "_blank")
  }

  useEffect(() => {
    fetchReplenishments()
  }, [status])

  useEffect(() => {
    if (!search) {
      setFilteredReplenishments(replenishments)
      return
    }

    setFilteredReplenishments(
      replenishments.filter(
        r =>
          (r.contractor && r.contractor.toLocaleLowerCase().includes(search.toLocaleLowerCase())) ||
          (r.country && r.country.toLocaleLowerCase().includes(search.toLocaleLowerCase())) ||
          (r.amount && round2Digits(Number(r.amount) / 100).toString() === search.toLocaleLowerCase()) ||
          (r.id && r.id.toString() === search) ||
          (r.currency && r.currency.toLocaleLowerCase() === search.toLocaleLowerCase())
      )
    )
  }, [search])

  useEffect(() => {
    fetchUnread()

    const intervalId = setInterval(() => {
      fetchUnread()
    }, CONFIG.fetchUnreadsInterval)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return (
    <>
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />
      <MenuClient active={"replenishment"} />
      <main className="main-container" id="scrollable">
        <Header />
        <div className="container">
          <h1 className="ui-heading">Пополнения рублевые</h1>

          <section className="header-chapter">
            <div className="headr-chapter-name">
              <div className="header-chapter-icon">
                <img src={IMG.deals} alt="" className="header-chapter-img" />
              </div>
              <Button variant="black" onClick={refetchData} loading={isRefetchLoading}>
                Обновить список пополнений
              </Button>
            </div>
            <div className="flex-row-10 flex-wrap-mobile">
              <Button onClick={handleDownload} variant="black" loading={isDownloading}>
                Сверка пополнений
              </Button>
              <Button
                onClick={() => navigate(REPLENISHMENT_NEW_ROUTE)}
                leftIcon={<img src={IMG.plus_btn} alt="" className="img-btn" />}
              >
                Добавить
              </Button>
            </div>
          </section>

          <section className="search-block">
            <Input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по контрагенту, стране, сумме или валюте"
            />
          </section>

          <div className="main-content--wrapper">
            <section className="main-content">
              <ul className="header-global-list">
                <li className="header-global-item width-130">Дата и номер</li>
                <li className="header-global-item cols-deals">Название и страна</li>
                <li className="header-global-item cols-deals">Компания плательщик</li>
                <li className="header-global-item width-130">Сумма</li>
                <li className="header-global-item width-100">Валюта</li>
                <li className="header-global-item width-70">Ставка</li>
                <li className="header-global-item width-130">
                  <select value={status} className="select-style" onChange={e => setStatus(e.target.value)}>
                    <option value="" key="EMPTY">
                      Статус
                    </option>
                    <option value="ACTION_REQUIRED" key="ACTION_REQUIRED">
                      Требуется действие
                    </option>
                    {Object.keys(ReplenishmentStatus).map((key, index) =>
                      ReplenishmentStatus[key as keyof IReplenishmentStatus][1] ? (
                        <option value={key} key={index}>
                          {ReplenishmentStatus[key as keyof IReplenishmentStatus][1]}
                        </option>
                      ) : null
                    )}
                  </select>
                </li>
              </ul>
              <ul className="global-list">
                {isLoading && (
                  <div className="skeleton-wrapper">
                    <Skeleton rows={4} gutterTop />
                  </div>
                )}
                {!isLoading &&
                  (filteredReplenishments.length > 0 ? (
                    filteredReplenishments.map((r, index) => (
                      <li className={getRowClazz(r.status)} id="deals-info-open" key={index}>
                        <div className="global-scroll-block">
                          <p className="global-item-text width-130" onClick={() => navigateToReplenishment(r.id)}>
                            <span>№{r.id}</span>
                            <span dangerouslySetInnerHTML={{ __html: getMskTime(r.createdAt || "") || "" }} />
                          </p>
                          <p className="deal-item-flex cols-deals" onClick={() => navigateToReplenishment(r.id)}>
                            <span className="deal-country">{r.country?.toUpperCase() || "-"}</span>
                            <span className="deal-name">{r.contractor || "-"}</span>
                          </p>
                          <p className="deal-item-flex cols-deals" onClick={() => navigateToReplenishment(r.id)}>
                            <span className="deal-country">{r.companyCountry?.toUpperCase() || "-"}</span>
                            <span className="deal-name">{r.companyName || "-"}</span>
                          </p>
                          <p className="global-item-text width-130" onClick={() => navigateToReplenishment(r.id)}>
                            {r.amount ? spacedNum(round2Digits(Number(r.amount) / 100)) : "-"}
                          </p>
                          <p
                            className="global-item-text globak-num width-100"
                            onClick={() => navigateToReplenishment(r.id)}
                          >
                            {r.currency || "-"}
                          </p>
                          <p className="global-item-text width-70" onClick={() => navigateToReplenishment(r.id)}>
                            {r.rate ? `${r.rate} %` : "-"}
                          </p>
                          <p
                            className={`${getStatusClazz(r.status)} width-130`}
                            onClick={() => navigateToReplenishment(r.id)}
                          >
                            {r.status ? ReplenishmentStatus[r.status as keyof IReplenishmentStatus][1] : ""}
                          </p>
                        </div>
                        <button className="chat-deals" onClick={() => showMessenger(r.id?.toString(), r.userId)}>
                          {getUnreadCounter(r.id?.toString()) > 0 ? (
                            <span className="message-new">{getUnreadCounter(r.id?.toString())}</span>
                          ) : (
                            ""
                          )}
                        </button>
                      </li>
                    ))
                  ) : (
                    <EmptyRow text="Пополнений не найдено" />
                  ))}
              </ul>
            </section>
          </div>
        </div>
        <Messenger instanceId={instanceId} type={CHAT_INSTANCE} userId={conversationUserId} close={closeMessenger} />
      </main>
    </>
  )
}
